import {
  InventoryPurchaseConfig,
  ProductInventoryDoc,
  ProductOutboundTransaction,
  ProductSupplierDoc,
  ProductSupplierPurchaseOrderDoc,
  PurchaseBundleType,
  SMSPostRequest,
} from '@gooduncles/gu-app-schema';
import { message } from 'antd';
import { formatDate } from 'date-fns';
import { countBy, groupBy, round } from 'lodash';
import { isProduction, sendNo } from 'src/constants';

import { errorObjectToString } from 'src/utils/common';
import { updateProductSupplierPurchaseOrderMessageBody } from 'src/utils/firebase/firebase-short-cut';

/**
 * 발주서에 거래처를 연결한 타입으로 발주관리에서 사용합니다.
 */
export type PurchaseOrderWithProductSupplier = ProductSupplierPurchaseOrderDoc & {
  productSupplier: ProductSupplierDoc | undefined;
};

/**
 * 발주 메시지 앞뒤로 붙는 안내문구입니다.
 */
export type MessageWrapper = {
  messagePrefix: string;
  messageSuffix: string;
  date: string;
  purchaseOrderInquiryNumber: string;
};

/**
 * 발주 요청량을 발주 설정에 맞춘 수량으로 변환합니다.
 * @param outboundTransactions
 * @param purchaseMultiplyUnit
 * @param purchaseMaxBundle
 * @returns
 */
const getQuantityForSupplierConversion = (
  outboundTransactions: ProductOutboundTransaction[],
  purchaseMultiplyUnit: number,
  purchaseMaxBundle: number
) => {
  const totalVolume = outboundTransactions.reduce(
    // 1.5 * 1000 = 1500
    (acc, txn) => acc + txn.requestQuantity * purchaseMultiplyUnit,
    0
  );
  const divider = purchaseMaxBundle;
  const quotient = Math.floor(totalVolume / divider);
  const remainder = totalVolume % divider;
  return { remainder, amount: quotient, bundle: divider };
};

/**
 * 거래처의 묶음 규칙을 적용한 목록을 가져온다.
 */
const getBundleList = (
  outboundTransactions: ProductOutboundTransaction[],
  purchaseMultiplyUnit: number,
  purchaseMaxBundle: number
) => {
  /**
   * 수량 묶음은 모든 상품 합계에 대한 것이 아닌
   * 매장별로 나뉘어 계산되어야한다.
   * 예)
   * - A매장: 손질양배추(1개/대/국내산) - 9개
   * - B매장: 손질양배추(1개/대/국내산) - 5개
   * - C매장: 손질양배추(1개/대/국내산) - 4개
   * 발주 메시지 -> 손질양배추 1개짜리 x 1 / 2개짜리 x 1 / 3개짜리 x 5
   */
  const groupByStore = groupBy(outboundTransactions, (txn) => txn.storeId);
  return Object.values(groupByStore).map((bySupplier) =>
    getQuantityForSupplierConversion(bySupplier, purchaseMultiplyUnit, purchaseMaxBundle)
  );
};

/**
 * 묶음과 수량 목록을 메시지로 변환한다.
 * @param bundleList [묶음, 수량][]
 * @param purchaseUnit 단위 (g, kg, ...)
 * @returns
 */
const toQuantityMessage = (
  bundleList: { remainder: number; amount: number; bundle: number }[],
  purchaseUnit: string
) => {
  const volumeByStore = bundleList.reduce((acc, i) => {
    if (acc[i.remainder]) {
      acc[i.remainder] = acc[i.remainder] + 1;
    } else {
      acc[i.remainder] = 1;
    }

    if (acc[i.bundle]) {
      acc[i.bundle] = acc[i.bundle] + i.amount;
    } else {
      acc[i.bundle] = i.amount;
    }

    return acc;
  }, {} as Record<number, number>);

  return Object.entries(volumeByStore)
    .filter(([bundle, amount]) => Number(bundle) > 0 && amount > 0)
    .map(([bundle, amount]) => `${bundle}${purchaseUnit} x ${amount}`)
    .join('\n');
};

/**
 * 발주 설정에 맞춰서 출고 요청을 하나의 발주 메시지로 변환합니다.
 */
export const generatePurchaseOrderMessage = (
  inventory: ProductInventoryDoc,
  purchaseTransactions: ProductOutboundTransaction[]
) => {
  const purchaseProductName = inventory.purchaseConfig.purchaseProductName ?? inventory.inventoryName;
  const purchaseUnit = inventory.purchaseConfig.purchaseUnit ?? '';
  const purchaseMultiplyUnit = inventory.purchaseConfig.purchaseMultiplyUnit;
  const purchaseMaxBundle = inventory.purchaseConfig.purchaseMaxBundle;
  const purchaseBundle = inventory.purchaseConfig.purchaseBundle;

  /**
   * 1) 묶음별 분할
   * A매장: 손질양배추(1개/대/국내산) - 9개
   * B매장: 손질양배추(1개/대/국내산) - 5개
   * C매장: 손질양배추(1개/대/국내산) - 4개
   * -> 손질양배추
   * 1개짜리 x 1
   * 2개짜리 x 1
   * 3개짜리 x 5
   */
  if (purchaseBundle === 'max' && purchaseMaxBundle) {
    const bundleForSupplier = getBundleList(purchaseTransactions, purchaseMultiplyUnit, purchaseMaxBundle);
    const quantityMessage = toQuantityMessage(bundleForSupplier, purchaseUnit);
    return `${purchaseProductName}\n${quantityMessage}`;
  }

  /**
   * 2) 총 합계(실수량 합계)
   * A매장: 백오이(5개/국내산) - 2개
   * B매장: 백오이(5개/국내산) - 2개
   * C매장: 백오이(5개/국내산) - 1개
   * -> 백오이 25개
   */
  if (purchaseBundle === 'all') {
    const totalQuantity = purchaseTransactions.reduce(
      (acc, cur) => acc + round(cur.requestQuantity * purchaseMultiplyUnit, 2),
      0
    );
    return `${purchaseProductName} ${totalQuantity}${purchaseUnit}`;
  }

  /**
   * 3) 매장별 합계
   * A매장: 감자(1kg/왕특/국내산) - 3개
   * B매장: 감자(1kg/왕특/국내산) - 1개
   * C매장: 감자(1kg/왕특/국내산) - 4개
   * -> 감자 왕특(kg) 1, 3, 4 (총 8kg)
   */
  if (purchaseBundle === 'store') {
    const totalQuantity = purchaseTransactions.reduce(
      (acc, cur) => acc + cur.requestQuantity * purchaseMultiplyUnit,
      0
    );

    const groupByStore = groupBy(purchaseTransactions, (txn) => txn.storeId);
    const quantityMessage = Object.values(groupByStore)
      .map((bySupplier) => bySupplier.reduce((acc, product) => acc + product.requestQuantity * purchaseMultiplyUnit, 0))
      .sort();
    return `${purchaseProductName} ${quantityMessage.join(', ')} (총 ${totalQuantity}${purchaseUnit})`;
  }

  /**
   * 4) 매장별 수량 묶음
   * A매장: 대파 (A급/1단/국내산) 1kg - 1개
   * B매장: 대파 (A급/1단/국내산) 1kg - 1개
   * C매장: 대파 (A급/1단/국내산) 1kg - 1개
   * D매장: 대파 (A급/1단/국내산) 1kg - 2개
   * -> 대파
   * 1단 x 3
   * 2단 x 1
   * (총 5단)
   */
  if (purchaseBundle === 'storeGroup') {
    const totalQuantity = purchaseTransactions.reduce(
      (acc, cur) => acc + cur.requestQuantity * purchaseMultiplyUnit,
      0
    );

    const groupByStore = groupBy(purchaseTransactions, (txn) => txn.storeId);
    const quantities = Object.values(groupByStore).map((bySupplier) =>
      bySupplier.reduce((acc, product) => acc + product.requestQuantity * purchaseMultiplyUnit, 0)
    );
    const countByQuantity = countBy(quantities);
    const quantityMessage = Object.entries(countByQuantity)
      .sort()
      .map(([quantity, count]) => `${quantity}${purchaseUnit} x ${count}`)
      .join('\n');
    return `${purchaseProductName}\n${quantityMessage}\n(총 ${totalQuantity}${purchaseUnit})`;
  }

  return `변환 실패 - 상품 재고 정보: ${inventory._id}`;
};

/**
 * 발주 메시지 미리보기에 사용되는 메시지 래퍼를 가져온다.
 * @param date 발주(배송) 일자
 */
export const getMessageWrapper = (date: Date | null, purchaseOrderInquiryNumber = ''): MessageWrapper => {
  if (!date) {
    return { messagePrefix: '', messageSuffix: '', date: '', purchaseOrderInquiryNumber };
  }
  const messagePrefix = `안녕하세요. 이웃삼촌입니다.\n${formatDate(new Date(date), 'L월 d일')} 주문 드립니다.\n\n`;
  const messageSuffix = '\n\n감사합니다.';
  return { messagePrefix, messageSuffix, date: formatDate(new Date(date), 'yyyy-MM-dd'), purchaseOrderInquiryNumber };
};

/**
 * 발주서를 SMS 요청 데이터로 변환합니다.
 */
export const convertPurchaseOrderToSMSPostRequest = (
  purchaseOrder: PurchaseOrderWithProductSupplier,
  messageWrapper: MessageWrapper
): (SMSPostRequest & { purchaseOrderId: string }) | string => {
  const { productSupplier, messageBody } = purchaseOrder;
  const { messagePrefix, messageSuffix } = messageWrapper;
  if (!productSupplier) {
    return purchaseOrder._id;
  }
  const { name, primaryContact, additionalContacts } = productSupplier;

  const contacts =
    additionalContacts.length > 0 ? [primaryContact, ...additionalContacts.map((c) => c.number)] : [primaryContact];
  const request: SMSPostRequest & { purchaseOrderId: string } = {
    body: `${messagePrefix}${messageBody}${messageSuffix}`,
    sendNo,
    recipientList: contacts.map((contact) => ({
      recipientNo: contact.replace(/-/g, ''),
      recipientGroupingKey: name,
    })),
    purchaseOrderId: purchaseOrder._id,
  };
  return request;
};

/**
 * 발주 메시지를 변경을 처리하며 그 이력을 남긴다.
 */
export const onPurchaseOrderMessageBodyChanged = async (params: any) => {
  const purchaseOrder = params.data as PurchaseOrderWithProductSupplier;
  const oldValue = params.oldValue;
  const { messageBody: newValue } = purchaseOrder;
  if (!newValue || newValue === oldValue) {
    message.info('변경사항이 없습니다.');
    return;
  }
  try {
    await updateProductSupplierPurchaseOrderMessageBody(purchaseOrder._id, oldValue, newValue);
    message.success('변경완료');
  } catch (error) {
    console.error(error);
    const errorMessage = errorObjectToString(error);
    message.error(errorMessage);
  }
};

/**
 * 발주 설정에 따른 미리보기 메시지를 생성합니다.
 * @param config 발주 설정
 * @param volume 예시 수량 (기본값: 10)
 */
export const getPurchasePreviewMessage = (config: InventoryPurchaseConfig, volume = 10) => {
  const { purchaseProductName, purchaseBundle, purchaseMaxBundle, purchaseMultiplyUnit, purchaseUnit } = config;
  const unit = purchaseUnit ?? '개';

  if (purchaseBundle === PurchaseBundleType.ALL) {
    return `${purchaseProductName} ${volume * purchaseMultiplyUnit}${unit}`;
  }

  if (purchaseBundle === PurchaseBundleType.STORE) {
    const sample = [1, 2, 3, 4];
    const total = sample.reduce((acc, cur) => acc + cur * purchaseMultiplyUnit, 0);
    return `${purchaseProductName} ${sample.map((s) => s * purchaseMultiplyUnit).join(', ')} (총 ${total}${unit})`;
  }

  if (purchaseBundle === PurchaseBundleType.STORE_GROUP) {
    const sample = [1, 1, 2, 2, 4];
    const countByQuantity = countBy(sample);
    const quantityMessage = Object.entries(countByQuantity)
      .sort()
      .map(([quantity, count]) => `${quantity}${unit} x ${count}`)
      .join('\n');
    const total = sample.reduce((acc, cur) => acc + cur * purchaseMultiplyUnit, 0);
    return `${purchaseProductName}\n${quantityMessage}\n(총 ${total}${unit})`;
  }

  if (purchaseBundle === PurchaseBundleType.MAX) {
    if (!purchaseMaxBundle) {
      return '⚠️ 최대 묶음 값을 입력해주세요.';
    }
    const totalVolume = volume * purchaseMultiplyUnit;
    const bundle = purchaseMaxBundle;
    const amount = Math.floor(totalVolume / bundle);
    const remainder = totalVolume % bundle;
    const quantityMessage = toQuantityMessage([{ remainder, amount, bundle }], unit);
    return `${purchaseProductName}\n${quantityMessage}`;
  }

  return '';
};

export const downloadPurchaseOrderExcelFile = async (token: string, purchaseOrderId: string) => {
  try {
    const response = await fetch(
      isProduction
        ? 'https://httpproductsupplierpurchaseorderexcel-ih3qpz67eq-uc.a.run.app'
        : 'https://httpproductsupplierpurchaseorderexcel-46vxdyu3eq-uc.a.run.app',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ purchaseOrderId }),
      }
    );
    // 응답이 Blob인 경우 (파일 다운로드)
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    let filename = `발주서_${purchaseOrderId}.xlsx`;
    const contentDisposition = response.headers.get('Content-Disposition');
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"|filename\*=UTF-8''([^;]+)/i);
      if (filenameMatch) {
        const encodedFilename = filenameMatch[1] || filenameMatch[2];
        filename = decodeURIComponent(encodedFilename);
      }
    }
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  } catch (error) {
    console.error(error);
    const errorMessage = errorObjectToString(error);
    message.error(errorMessage);
  }
};
